import React, { useCallback } from 'react';
import { IconProps } from '@landr/maestro';
import { t } from '@lingui/macro';
import {
    RequestCostPreviewQueries,
    RequestCostPreviewQueryItemName,
} from 'apollo/cost';
import {
    getDownloadIcon,
    getDownloadLabel,
} from 'components/CommonUi/DownloadButton/DownloadButton.helpers';
import { usePurchaseControllerContext } from 'contexts/PurchaseControllerContext/PurchaseControllerContext';
import { useSubscriptionsAndAllowedFeaturesContext } from 'contexts/SubscriptionsAndAllowedFeaturesContext/SubscriptionsAndAllowedFeaturesContext';
import { getDownloadableItem } from 'helpers';
import { useFreeUserFreeDownloadUpsellModalContext } from 'modals/UpsellModals/FreeUserFreeDownloadUpsellModalContext';
import { AuthenticatedActions, DownloadableItemSource } from 'types';
import { DownloadType } from 'types/enums';
import { useApolloClient } from '@apollo/client';
import { useExecutionEnvironmentContext } from 'contexts/ExecutionEnvironmentContext/ExecutionEnvironmentContext';
const useDownloadButton = ({
    item,
    onClick,
    label,
    showIcon = true,
}: {
    item: DownloadableItemSource;
    onClick?: () => void;
    label: string;
    showIcon?: boolean;
}): {
    handleClick: (event?: React.MouseEvent<Element>) => void;
    isLoading: boolean;
    isDownloadable: boolean;
    icon: React.FC<IconProps> | undefined;
    buttonLabel: string;
    userAction: any;
} => {
    const apolloClient = useApolloClient();
    const downloadableItem = getDownloadableItem(item);
    const { isDAWPlugin } = useExecutionEnvironmentContext();

    const icon = showIcon
        ? getDownloadIcon(downloadableItem.isDownloaded, isDAWPlugin)
        : undefined;

    const buttonLabel = getDownloadLabel(
        item,
        downloadableItem,
        isDAWPlugin,
        label,
    );

    const isDownloadable = downloadableItem.type !== DownloadType.Unknown;

    const userAction = {
        type: AuthenticatedActions.downloadItem,
        description: t`download`,
        args: [downloadableItem],
    };

    const {
        startPurchase,
        isLoading: purchaseLoading,
        queuedItemIDs,
    } = usePurchaseControllerContext();

    const isLoading =
        purchaseLoading && queuedItemIDs.includes(downloadableItem.id);

    const { upsellingAllowed } = useSubscriptionsAndAllowedFeaturesContext();

    const { openModal } = useFreeUserFreeDownloadUpsellModalContext();

    const handleClick = useCallback(() => {
        async function execute() {
            const shouldUseId = [
                DownloadType.Pack,
                DownloadType.Repack,
                DownloadType.Collection,
                DownloadType.CollectionMulti,
            ].includes(downloadableItem.type);

            const { data } = await apolloClient.query({
                query: RequestCostPreviewQueries[downloadableItem.type],
                fetchPolicy: 'no-cache',
                variables: {
                    guid: shouldUseId
                        ? downloadableItem.id
                        : downloadableItem.itemIds,
                },
            });

            const costPreviewData =
                data[RequestCostPreviewQueryItemName[downloadableItem.type]];

            const isFree = costPreviewData.credits === 0;

            if (upsellingAllowed && isFree) {
                openModal(downloadableItem);
            } else {
                startPurchase({ item: downloadableItem });
            }

            onClick?.();
        }

        execute();
    }, [
        downloadableItem,
        apolloClient,
        upsellingAllowed,
        onClick,
        openModal,
        startPurchase,
    ]);

    return {
        handleClick,
        isLoading,
        isDownloadable,
        icon,
        buttonLabel,
        userAction,
    };
};

export default useDownloadButton;
